import { DataAnnotation, filterAnnotationsAfterReset, isManualAnnotation, isPathologyAnnotation, PathologistAnnotation } from '@/app/types'
import { AnnotationDataFieldValue, SPECIMEN_TRACKS, TUMOR_PRESENCES } from '@/app/types/enums'
import { DISPLAY_TIMEZONE } from '@/lib/display-timezone'
import { parseAnnotationTimestamp, parseNaiveUtcTimestamp } from '@/lib/parse-annotation-timestamp'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { v4 } from 'uuid'

export { parseAnnotationTimestamp, parseNaiveUtcTimestamp }

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()

export const extractProvider = (key: string): string | null => {
  return extractFromKey(key, 0)
}

export const extractName = (key: string): string | null => {
  return extractFromKey(key, 1)
}

const extractFromKey = (key: string, idx: number): string | null => {
  const regex = /([^/]+)\//
  const match = key.match(regex)
  if (!match) return null
  const parts = key.split('/')
  return parts.length > idx ? parts[idx] : null
}

/**
 * The acquisition's folder in storage — the last segment of its object prefix, e.g.
 * `20260824_142627_FBI-2961300-`. This is the name the device wrote, and the only
 * human-recognisable identifier an acquisition carries besides its code.
 */
export const storageFolderName = (url?: string | null): string => {
  const path = (url ?? '').split('?')[0].replace(/\/+$/, '')
  return path.slice(path.lastIndexOf('/') + 1)
}

/** The annotation values stored as bare enum keys, which are also their i18n keys. */
const LOCALIZED_ANNOTATION_VALUES = new Set<string>([...SPECIMEN_TRACKS, ...TUMOR_PRESENCES])

/**
 * Render a consolidated annotation value in the reader's language.
 *
 * `extractAnnotationField` returns the stored value, which for the coded fields is a
 * bare enum key. Anything outside the map — free text, an ICD-O-3 topography, the
 * already-localized `t('multiple')` sentinel — passes through untouched.
 */
export const localizeAnnotationValue = (value: string, t: (key: string) => string): string => {
  return LOCALIZED_ANNOTATION_VALUES.has(value) ? t(value) : value
}

/** Select the best annotation: manual annotations have highest priority, then latest by created date.
 *  Annotations before the most recent ResetAnnotation are ignored. */
export const selectBestAnnotation = (annotations: PathologistAnnotation[]): DataAnnotation | undefined => {
  const effective = filterAnnotationsAfterReset(annotations)
  if (effective.length === 0) return undefined
  const manualAnnotations = effective.filter(isManualAnnotation)
  const candidates = manualAnnotations.length > 0 ? manualAnnotations : effective
  return candidates.sort((a, b) => {
    const dateA = parseAnnotationTimestamp(a.created).getTime()
    const dateB = parseAnnotationTimestamp(b.created).getTime()
    return dateB - dateA
  })[0]
}

export type ConsolidatedAnnotationField = { kind: 'value'; value: string } | { kind: 'multiple' } | { kind: 'none' }

/** Collapse an acquisition's annotation history into a single field value, mirroring how the
 *  column cells render: the latest manual annotation wins, otherwise pathology consensus.
 *  Annotations before the most recent ResetAnnotation are ignored. This is the single source of
 *  truth for the raw/detail path, which is the only one that still loads the history — the list
 *  path reads the API's own consolidated annotation via `annotationFieldValue`. */
export const consolidateAnnotationField = (
  annotations: PathologistAnnotation[] | null | undefined,
  fieldName: AnnotationDataFieldValue
): ConsolidatedAnnotationField => {
  if (!annotations || annotations.length === 0) return { kind: 'none' }
  const effective = filterAnnotationsAfterReset(annotations)
  if (effective.length === 0) return { kind: 'none' }

  const getFieldValue = (annotation: PathologistAnnotation): unknown => (annotation as unknown as Record<string, unknown>)[fieldName]

  // First priority: the latest manual annotation.
  const manualAnnotations = effective.filter((l) => isManualAnnotation(l))
  if (manualAnnotations.length > 0) {
    const latestManualAnnotation = manualAnnotations.sort((a, b) => {
      const dateA = parseAnnotationTimestamp(a.created).getTime()
      const dateB = parseAnnotationTimestamp(b.created).getTime()
      return dateB - dateA
    })[0]
    const value = getFieldValue(latestManualAnnotation)
    return value ? { kind: 'value', value: String(value) } : { kind: 'none' }
  }

  // Second priority: pathology consensus.
  const pathologyAnnotations = effective.filter((l) => isPathologyAnnotation(l) && getFieldValue(l))
  if (pathologyAnnotations.length > 0) {
    const uniqueValues = [...new Set(pathologyAnnotations.map((l) => String(getFieldValue(l))))]
    if (uniqueValues.length === 1) return { kind: 'value', value: uniqueValues[0] }
    if (uniqueValues.length > 1) return { kind: 'multiple' }
  }

  return { kind: 'none' }
}

export const extractAnnotationField = (
  annotations: PathologistAnnotation[] | null | undefined,
  fieldName: AnnotationDataFieldValue,
  t: (key: string) => string
): string => {
  const consolidated = consolidateAnnotationField(annotations, fieldName)
  switch (consolidated.kind) {
    case 'value':
      return consolidated.value
    case 'multiple':
      return t('multiple')
    case 'none':
      return ''
  }
}

/** A field's value on the consolidated annotation the API denormalizes onto every acquisition
 *  (`acquisition.annotation`). The list path reads this instead of re-consolidating a history it
 *  no longer fetches; '' means "no value", which is the "unknown" bucket for filters and facets.
 *
 *  Note: the backend drops a field whose pathology annotations disagree, so '' also covers "no
 *  consensus" — the `multiple` case `consolidateAnnotationField` still distinguishes on the raw
 *  path. Unreachable today (no tenant holds pathology records); revisit here if that changes. */
export const annotationFieldValue = (annotation: PathologistAnnotation | null | undefined, fieldName: AnnotationDataFieldValue): string => {
  const value = annotation ? (annotation as unknown as Record<string, unknown>)[fieldName] : undefined
  return value ? String(value) : ''
}

/** Whether the consolidated annotation's value for `fieldName` was entered by hand. The backend
 *  consolidates by preferring the latest manual annotation over pathology consensus, so the
 *  consolidated annotation's own type is the field's source. */
export const isManualAnnotationField = (
  annotation: PathologistAnnotation | null | undefined,
  fieldName: AnnotationDataFieldValue
): boolean => !!annotation && isManualAnnotation(annotation) && !!annotationFieldValue(annotation, fieldName)

export const correlationId = () => v4().replace(/-/g, '').slice(0, 16)

/**
 * `timeZone` defaults to the display zone. Pass `'UTC'` for a value that carries no
 * zone of its own — a `YYYY-MM-DD` parsed as UTC midnight, say — where rendering it
 * in the display zone would shift it a day for any zone behind UTC (#967).
 */
export const formatDateShort = (d: Date, timeZone: string = DISPLAY_TIMEZONE) => {
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
  return format.format(d)
}

export const formatTimeAgo = (d: Date, t: (key: string, values?: Record<string, string | number | Date>) => string): string => {
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffMs < 0) return t('time_ago_just_now')
  if (diffMinutes < 1) return t('time_ago_just_now')
  if (diffMinutes < 60) return t('time_ago_minutes', { count: diffMinutes })
  if (diffHours < 24) return t('time_ago_hours', { count: diffHours })
  if (diffDays < 7) return t('time_ago_days', { count: diffDays })
  if (diffWeeks < 5) return t('time_ago_weeks', { count: diffWeeks })
  if (diffMonths < 12) return t('time_ago_months', { count: diffMonths })
  return formatDateShort(d)
}

export const formatDateShortDots = (d: Date) => {
  const format = new Intl.DateTimeFormat('de-CH', {
    timeZone: DISPLAY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return format.format(d)
}

export const formatDateTimeShort = (d: Date) => {
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  return format.format(d)
}

export const formatDateTimeDeCH = (d: Date, timeZone: string = DISPLAY_TIMEZONE) => {
  const format = new Intl.DateTimeFormat('de-CH', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  return format.format(d)
}

export const formatNumber = (d: number) => {
  const format = new Intl.NumberFormat('en-GB')
  return format.format(d)
}

/** Stand-in for a value that is not recorded — an en dash reads as "none", a blank as a bug. */
export const EM_DASH = '—'

export const isTestOrDev = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
export const isProd = !isTestOrDev

// a wrapper function for console.log, so the code should not contain any console.log and if we need console.log
// then use this wrapper, so we know its intentional!
export const consoleInfo = (...args: unknown[]) => console.log('[INFO]', ...args)
export const consoleDebug = (...args: unknown[]) => console.debug('%c[DEBUG]', 'color: blue; font-weight: bold', ...args)
export const consoleWarn = (...args: unknown[]) => console.warn('%c[WARN]', 'color: orange; font-weight: bold', ...args)
export const consoleError = (...args: unknown[]) => console.error('%c[ERROR]', 'color: red; font-weight: bold', ...args)

/**
 * Parses and formats a raw metadata timestamp to a localized date-time string
 * (de-CH). Handles both the legacy underscore form
 * `YYYY_MM_DD__HH_MM_SS_microseconds` (device-local wall clock, rendered
 * verbatim) and the newer ISO-8601 form
 * (e.g. `2026-07-16T16:06:51.207318+02:00`, whose offset is respected).
 * @param raw - Raw timestamp string in either supported format
 * @returns Formatted date-time string, or the raw value / 'N/A' if unparseable
 */
export const parseMetadataTimestamp = (raw?: string): string => {
  if (!raw) return 'N/A'

  // Legacy underscore form: YYYY_MM_DD__HH_MM_SS_microseconds — the device's
  // local wall clock, no offset (#831). Rendered verbatim for every viewer: built
  // and formatted in the same zone, so the digits pass straight through. UTC is
  // arbitrary here and only has to match on both lines — this value names no zone,
  // so converting it to one would invent an offset it never carried.
  const match = raw.match(/^(\d{4})_(\d{2})_(\d{2})__([0-2]\d)_(\d{2})_(\d{2})_(\d+)$/)

  if (match) {
    const [, year, month, day, hour, minute, second] = match
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)))
    if (isNaN(date.getTime())) return raw
    return formatDateTimeDeCH(date, 'UTC')
  }

  // Newer ISO-8601 form — parse respecting its offset (offset-less ⇒ UTC).
  const isoDate = parseNaiveUtcTimestamp(raw)
  if (!isNaN(isoDate.getTime())) return formatDateTimeDeCH(isoDate)

  return raw
}

/**
 * Triggers a browser download by creating a temporary anchor element
 * @param downloadUrl - The URL to download from
 * @param filename - The suggested filename for the download
 */
export function triggerBrowserDownload(downloadUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
